import { DEFAULT_SCALE, PRESETS, PRESET_ORDER } from './config/presets';
import { delayPairLabel, parseDelaySpec } from './lib/delay';
import { convertToApng, downloadBytes } from './lib/pipeline';
import type { DelayPair, JobConfig, OutputExt, PresetId } from './lib/types';

interface FileSlot {
  file: File | null;
  url: string | null;
}

function revoke(slot: FileSlot): void {
  if (slot.url) URL.revokeObjectURL(slot.url);
  slot.url = null;
  slot.file = null;
}

export function mountGenerateApp(root: HTMLElement): () => void {
  const contentSlot: FileSlot = { file: null, url: null };
  const coverSlot: FileSlot = { file: null, url: null };
  let lastResult: Awaited<ReturnType<typeof convertToApng>> | null = null;
  let busy = false;

  root.innerHTML = `
    <details class="privacy card">
      <summary>隐私说明（点击展开）</summary>
      <p>所有图片均在浏览器本地完成解码与 APNG 编码，不会上传到服务器。关闭页面前请自行下载保存结果。</p>
    </details>
    <section class="card">
      <h2>预设</h2>
      <div class="preset-grid" id="preset-grid" role="radiogroup" aria-label="预设"></div>
    </section>
    <section class="card">
      <h2>图片</h2>
      <div class="row two">
        <div class="field">
          <label>内容图（必选）</label>
          <div class="dropzone" id="dz-content">
            <input type="file" id="file-content" accept="image/png,image/jpeg,image/webp,image/bmp,image/gif,image/*" />
            <span class="hint">点击或拖放 PNG / JPEG / WebP…</span>
            <img class="thumb" id="thumb-content" alt="" hidden />
            <span class="fname" id="name-content"></span>
          </div>
        </div>
        <div class="field">
          <label>封面图（封面预设必选）</label>
          <div class="dropzone" id="dz-cover">
            <input type="file" id="file-cover" accept="image/png,image/jpeg,image/webp,image/bmp,image/gif,image/*" />
            <span class="hint">封面会缩放到内容图尺寸</span>
            <img class="thumb" id="thumb-cover" alt="" hidden />
            <span class="fname" id="name-cover"></span>
          </div>
          <p class="hint-inline" id="cover-hint">当前预设需要封面图；请勿使用纯黑封面。</p>
        </div>
      </div>
    </section>
    <section class="card">
      <h2>参数</h2>
      <div class="row two">
        <div class="field"><label for="scale">缩放 scale（相对原图）</label><input type="number" id="scale" min="0.05" max="4" step="0.05" value="${DEFAULT_SCALE}" /></div>
        <div class="field"><label for="loop">循环 loop（0 = 无限）</label><input type="number" id="loop" min="0" max="65535" step="1" value="1" /></div>
        <div class="field"><label for="delay0">帧0 delay（ms 或 num/den）</label><input type="text" id="delay0" value="60/1000" autocomplete="off" /></div>
        <div class="field"><label for="delay1">帧1 delay（ms 或 num/den）</label><input type="text" id="delay1" value="60000/1000" autocomplete="off" /></div>
        <div class="field"><label for="ext">输出后缀</label><select id="ext"><option value=".apng" selected>.apng</option><option value=".png">.png</option></select></div>
      </div>
      <p class="hint-inline">非手动预设下 delay / loop 由预设填充（仍可改）。</p>
    </section>
    <section class="card">
      <h2>生成</h2>
      <div class="actions"><button type="button" class="primary" id="btn-gen">生成 APNG</button><button type="button" class="ghost" id="btn-dl" disabled>下载</button></div>
      <p class="status info" id="status" role="status" aria-live="polite"></p>
      <div class="meta-box" id="meta" hidden></div>
    </section>
  `;

  const presetGrid = root.querySelector('#preset-grid') as HTMLElement;
  for (const id of PRESET_ORDER) {
    const preset = PRESETS[id];
    const label = document.createElement('label');
    label.className = 'preset-option';
    label.innerHTML = `<input type="radio" name="preset" value="${preset.id}" ${id === 'cover_reveal' ? 'checked' : ''} /><span class="label">${preset.label}</span><p class="desc">${preset.desc}</p>`;
    presetGrid.appendChild(label);
  }

  const els = {
    scale: root.querySelector('#scale') as HTMLInputElement,
    loop: root.querySelector('#loop') as HTMLInputElement,
    delay0: root.querySelector('#delay0') as HTMLInputElement,
    delay1: root.querySelector('#delay1') as HTMLInputElement,
    ext: root.querySelector('#ext') as HTMLSelectElement,
    btnGen: root.querySelector('#btn-gen') as HTMLButtonElement,
    btnDl: root.querySelector('#btn-dl') as HTMLButtonElement,
    status: root.querySelector('#status') as HTMLElement,
    meta: root.querySelector('#meta') as HTMLElement,
    coverHint: root.querySelector('#cover-hint') as HTMLElement,
    fileContent: root.querySelector('#file-content') as HTMLInputElement,
    fileCover: root.querySelector('#file-cover') as HTMLInputElement,
    thumbContent: root.querySelector('#thumb-content') as HTMLImageElement,
    thumbCover: root.querySelector('#thumb-cover') as HTMLImageElement,
    nameContent: root.querySelector('#name-content') as HTMLElement,
    nameCover: root.querySelector('#name-cover') as HTMLElement,
    dzContent: root.querySelector('#dz-content') as HTMLElement,
    dzCover: root.querySelector('#dz-cover') as HTMLElement,
  };

  const selectedPreset = (): PresetId => (root.querySelector<HTMLInputElement>('input[name="preset"]:checked')?.value as PresetId) || 'cover_reveal';
  const setStatus = (text: string, kind: 'info' | 'ok' | 'error' = 'info'): void => {
    els.status.textContent = text;
    els.status.className = `status ${kind}`;
  };
  const delayMs = (delay: DelayPair): number => (delay[0] / (delay[1] || 100)) * 1000;

  function applyPreset(): void {
    const preset = PRESETS[selectedPreset()];
    if (preset.delays) {
      els.delay0.value = `${preset.delays[0][0]}/${preset.delays[0][1]}`;
      els.delay1.value = `${preset.delays[1][0]}/${preset.delays[1][1]}`;
    }
    if (preset.loop !== null) els.loop.value = String(preset.loop);
    const manual = preset.id === 'manual';
    els.delay0.readOnly = !manual;
    els.delay1.readOnly = !manual;
    els.coverHint.hidden = !preset.requireCover;
  }

  function bindDropzone(zone: HTMLElement, input: HTMLInputElement, slot: FileSlot, thumb: HTMLImageElement, name: HTMLElement): void {
    const select = (file?: File): void => {
      if (!file || !file.type.startsWith('image/')) return setStatus('请选择图片文件', 'error');
      revoke(slot);
      slot.file = file;
      slot.url = URL.createObjectURL(file);
      thumb.src = slot.url;
      thumb.hidden = false;
      name.textContent = file.name;
      lastResult = null;
      els.btnDl.disabled = true;
      els.meta.hidden = true;
      setStatus(`已选择：${file.name}`);
    };
    input.addEventListener('change', () => select(input.files?.[0]));
    zone.addEventListener('dragover', (event) => { event.preventDefault(); zone.classList.add('dragover'); });
    zone.addEventListener('dragleave', () => zone.classList.remove('dragover'));
    zone.addEventListener('drop', (event) => { event.preventDefault(); zone.classList.remove('dragover'); select(event.dataTransfer?.files?.[0]); });
  }

  bindDropzone(els.dzContent, els.fileContent, contentSlot, els.thumbContent, els.nameContent);
  bindDropzone(els.dzCover, els.fileCover, coverSlot, els.thumbCover, els.nameCover);
  root.querySelectorAll('input[name="preset"]').forEach((input) => input.addEventListener('change', () => { applyPreset(); lastResult = null; els.btnDl.disabled = true; }));
  applyPreset();

  function readConfig(): JobConfig {
    const d0 = parseDelaySpec(els.delay0.value);
    const d1 = parseDelaySpec(els.delay1.value);
    if (delayMs(d0) <= 0 || delayMs(d1) <= 0) throw new Error('delay 不能为 0');
    const scale = Number(els.scale.value);
    const loop = Number(els.loop.value);
    return {
      preset: selectedPreset(),
      scale: Number.isFinite(scale) && scale > 0 ? scale : 1,
      loop: Number.isFinite(loop) && loop >= 0 ? Math.trunc(loop) : 0,
      delays: [d0, d1],
      outputExt: (els.ext.value === '.png' ? '.png' : '.apng') as OutputExt,
    };
  }

  els.btnGen.addEventListener('click', async () => {
    if (busy) return;
    if (!contentSlot.file) return setStatus('请先选择内容图', 'error');
    try {
      const config = readConfig();
      if (PRESETS[config.preset].requireCover && !coverSlot.file) return setStatus('当前预设需要封面图', 'error');
      busy = true;
      els.btnGen.disabled = true;
      els.btnDl.disabled = true;
      els.meta.hidden = true;
      setStatus('正在本地编码 APNG…');
      const result = await convertToApng({ contentFile: contentSlot.file, coverFile: coverSlot.file, config });
      lastResult = result;
      els.btnDl.disabled = false;
      els.meta.hidden = false;
      els.meta.textContent = `${result.width}×${result.height} · ${result.filename}\n${result.metaSummary}\n期望 delay: ${delayPairLabel(result.delays[0])} / ${delayPairLabel(result.delays[1])}`;
      setStatus('生成成功，可下载', 'ok');
      downloadBytes(result.bytes, result.filename);
    } catch (error) {
      lastResult = null;
      setStatus(error instanceof Error ? error.message : '生成失败', 'error');
    } finally {
      busy = false;
      els.btnGen.disabled = false;
    }
  });
  els.btnDl.addEventListener('click', () => {
    if (lastResult) downloadBytes(lastResult.bytes, lastResult.filename);
  });

  return () => {
    revoke(contentSlot);
    revoke(coverSlot);
    lastResult = null;
  };
}
